import { z } from "zod"
import { isMexicanState } from "./mexicanStates"

// Common validation patterns
const phoneRegex = /^[+]?[1-9][\d]{0,15}$/
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Sanitization helpers
export const sanitizeString = (str: string) => {
  return str.trim().replace(/[<>]/g, "")
}

export const sanitizeEmail = (email: string) => {
  return email.trim().toLowerCase()
}

// User signup validation schemas
export const sellerSignupSchema = z.object({
  email: z.string().email("Email debe ser válido").transform(sanitizeEmail),
  password: z
    .string()
    .min(8, "Contraseña debe tener al menos 8 caracteres")
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      "Contraseña debe incluir al menos una mayúscula, una minúscula y un número"
    ),
  name: z
    .string()
    .min(2, "Nombre debe tener al menos 2 caracteres")
    .max(100, "Nombre no puede exceder 100 caracteres")
    .transform(sanitizeString),
  company: z
    .string()
    .min(2, "Nombre de empresa debe tener al menos 2 caracteres")
    .max(200, "Nombre de empresa no puede exceder 200 caracteres")
    .transform(sanitizeString),
  phone: z
    .string()
    .regex(phoneRegex, "Teléfono debe ser un número válido")
    .transform(sanitizeString),
  location: z.string().min(2, "Ubicación es requerida").transform(sanitizeString),
  industry: z.string().min(2, "Industria es requerida").transform(sanitizeString),
})

export const buyerSignupSchema = z.object({
  email: z.string().email("Email debe ser válido").transform(sanitizeEmail),
  password: z
    .string()
    .min(8, "Contraseña debe tener al menos 8 caracteres")
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      "Contraseña debe incluir al menos una mayúscula, una minúscula y un número"
    ),
  name: z
    .string()
    .min(2, "Nombre debe tener al menos 2 caracteres")
    .max(100, "Nombre no puede exceder 100 caracteres")
    .transform(sanitizeString),
  company: z
    .string()
    .min(2, "Nombre de empresa debe tener al menos 2 caracteres")
    .max(200, "Nombre de empresa no puede exceder 200 caracteres")
    .transform(sanitizeString),
  phone: z
    .string()
    .regex(phoneRegex, "Teléfono debe ser un número válido")
    .transform(sanitizeString),
  location: z.string().min(2, "Ubicación es requerida").transform(sanitizeString),
  industry: z.string().min(2, "Industria es requerida").transform(sanitizeString),
})

// Login validation schema
export const loginSchema = z.object({
  email: z.string().email("Email debe ser válido").transform(sanitizeEmail),
  password: z.string().min(1, "Contraseña es requerida"),
})

export const signupSchema = z.object({
  email: z.string().email("Email debe ser válido").transform(sanitizeEmail),
  password: z
    .string()
    .min(8, "Contraseña debe tener al menos 8 caracteres")
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      "Contraseña debe incluir al menos una mayúscula, una minúscula y un número"
    ),
  displayName: z
    .string()
    .min(2, "Nombre debe tener al menos 2 caracteres")
    .max(100, "Nombre no puede exceder 100 caracteres")
    .transform(sanitizeString),
})

export type SignupData = z.infer<typeof signupSchema>

// Buyer needs validation schema
export const buyerNeedsSchema = z.object({
  proteinType: z.string().min(1, "Tipo de proteína es requerido").transform(sanitizeString),
  corte: z
    .string()
    .max(100, "Corte no puede exceder 100 caracteres")
    .transform(sanitizeString)
    .optional(),
  quantity: z
    .number()
    .min(0.1, "Cantidad debe ser mayor a 0")
    .max(100000, "Cantidad no puede exceder 100,000 kg"),
  deliveryState: z.string().min(1, "Estado de entrega es requerido").transform(sanitizeString),
  deliveryCity: z
    .string()
    .min(1, "Ciudad de entrega es requerida")
    .max(100, "Ciudad no puede exceder 100 caracteres")
    .transform(sanitizeString),
  deliveryAddress: z
    .string()
    .min(5, "Dirección debe tener al menos 5 caracteres")
    .max(500, "Dirección no puede exceder 500 caracteres")
    .transform(sanitizeString),
  purchaseFrequency: z
    .string()
    .min(1, "Frecuencia de compra es requerida")
    .transform(sanitizeString),
  specifications: z
    .string()
    .max(1000, "Especificaciones no pueden exceder 1000 caracteres")
    .transform(sanitizeString)
    .optional(),
  comments: z
    .string()
    .max(1000, "Comentarios no pueden exceder 1000 caracteres")
    .transform(sanitizeString)
    .optional(),
})

// Catalog item validation schema
export const catalogItemSchema = z.object({
  cutType: z
    .string()
    .min(1, "Tipo de corte es requerido")
    .max(100, "Tipo de corte no puede exceder 100 caracteres")
    .transform(sanitizeString),
  price: z
    .number()
    .min(0.01, "Precio debe ser mayor a 0")
    .max(1000000, "Precio no puede exceder $1,000,000"),
  description: z
    .string()
    .max(1000, "Descripción no puede exceder 1000 caracteres")
    .transform(sanitizeString)
    .optional(),
})

// Supplier purchase validation schema
export const supplierPurchaseSchema = z.object({
  meat_type: z.string().min(1, "Tipo de carne es requerido").transform(sanitizeString),
  order_date: z.string().min(1, "Fecha es requerida"),
  supplier_name: z.string().min(1, "Nombre de proveedor es requerido").transform(sanitizeString),
  quantity_kg: z.number().min(0.01, "Cantidad debe ser mayor a 0"),
  price_per_kg: z.number().min(0.01, "Precio debe ser mayor a 0"),
  delivery_address: z.string().min(1, "Dirección es requerida").transform(sanitizeString),
  delivery_city: z.string().min(1, "Ciudad es requerida").transform(sanitizeString),
  delivery_state: z.string().min(1, "Estado es requerido"),
  logistics_notes: z.string().transform(sanitizeString).optional(),
  supplier_company: z.string().transform(sanitizeString).optional(),
  supplier_email: z
    .string()
    .email("Email debe ser válido")
    .transform(sanitizeEmail)
    .optional()
    .or(z.literal("")),
  supplier_phone: z.string().transform(sanitizeString).optional(),
})

// Client order validation schema
export const clientOrderSchema = z.object({
  meat_type: z.string().min(1, "Tipo de carne es requerido").transform(sanitizeString),
  order_date: z.string().min(1, "Fecha es requerida"),
  client_name: z.string().min(1, "Nombre de cliente es requerido").transform(sanitizeString),
  quantity_kg: z.number().min(0.01, "Cantidad debe ser mayor a 0"),
  price_per_kg: z.number().min(0.01, "Precio debe ser mayor a 0"),
  delivery_address: z.string().min(1, "Dirección es requerida").transform(sanitizeString),
  delivery_city: z.string().min(1, "Ciudad es requerida").transform(sanitizeString),
  delivery_state: z.string().min(1, "Estado es requerido"),
  logistics_notes: z.string().transform(sanitizeString).optional(),
  client_company: z.string().transform(sanitizeString).optional(),
  client_email: z
    .string()
    .email("Email debe ser válido")
    .transform(sanitizeEmail)
    .optional()
    .or(z.literal("")),
  client_phone: z.string().transform(sanitizeString).optional(),
})

export const baseAddressSchema = z.object({
  street: z.string().min(1, "Calle requerida"),
  externalNumber: z.string().min(1, "Número exterior requerido"),
  internalNumber: z.string().optional().or(z.literal("")),
  neighborhood: z.string().min(1, "Colonia requerida"),
  city: z.string().min(1, "Ciudad requerida"),
  state: z
    .string()
    .min(1, "Estado requerido")
    // Explicit `: boolean` return stops TS from inferring a type predicate (which
    // would narrow `state` to the literal union and break downstream form types).
    .refine((value): boolean => isMexicanState(value), "Selecciona un estado válido de México"),
  zipCode: z.string().regex(/^\d{5}$/, "Código postal debe tener 5 dígitos"),
  country: z.string().default("México").optional(),
})

export const userAddressSchema = z.object({
  receiverName: z.string().min(3, "Nombre completo requerido (mínimo 3 caracteres)"),
  phone: z.string().min(10, "Teléfono debe tener al menos 10 dígitos"),
  address: baseAddressSchema,
  isDefault: z.boolean().default(false).optional(),
})

export const warehouseSchema = z.object({
  name: z.string().min(3, "Nombre de almacén requerido"),
  address: baseAddressSchema,
})

export const providerSchema = z.object({
  name: z.string().min(2, "Nombre de proveedor requerido"),
  description: z.string().optional(),
  logoUrl: z.string().url("URL de logo inválida").optional().or(z.literal("")),
})

export type SellerSignupData = z.infer<typeof sellerSignupSchema>
export type BuyerSignupData = z.infer<typeof buyerSignupSchema>
export type LoginData = z.infer<typeof loginSchema>
export type BuyerNeedsData = z.infer<typeof buyerNeedsSchema>
export type CatalogItemData = z.infer<typeof catalogItemSchema>
export type SupplierPurchaseData = z.infer<typeof supplierPurchaseSchema>
export type ClientOrderData = z.infer<typeof clientOrderSchema>
export type BaseAddressData = z.infer<typeof baseAddressSchema>
export type UserAddressFormData = z.infer<typeof userAddressSchema>
export type WarehouseFormData = z.infer<typeof warehouseSchema>
export type ProviderFormData = z.infer<typeof providerSchema>

export const productSchema = z.object({
  name: z.string().min(3, "Nombre debe tener al menos 3 caracteres"),
  sku: z
    .string()
    .min(3, "SKU debe tener al menos 3 caracteres")
    .max(50)
    .optional()
    .or(z.literal("")),
  description: z.string().min(10, "Descripción debe tener al menos 10 caracteres"),
  price: z.coerce.number().positive("Precio debe ser mayor a 0"),
  unitCost: z.coerce.number().min(0, "Costo no puede ser negativo").default(0),
  weight: z.coerce.number().positive("Peso debe ser mayor a 0"),
  weightUnit: z.string().min(1, "Unidad de peso requerida"),
  initialStock: z.coerce.number().int().min(0, "Stock no puede ser negativo"),
  lowStockThreshold: z.coerce.number().int().min(0, "Umbral no puede ser negativo"),
  leadTimeDays: z.coerce.number().int().min(0).default(0),
  safetyStockLevel: z.coerce.number().int().min(0).default(0),
  isTaxable: z.boolean().default(false),
  consumptionType: z.enum(["Human", "Industrial"]).default("Human"),
  categoryId: z.coerce.number().positive("Selecciona una categoría válida"),
  providerId: z.coerce.number().positive("Selecciona un proveedor").optional().nullable(),
  warehouseId: z.coerce.number().positive("Selecciona un almacén inicial").optional().nullable(),
  imageFiles: z.array(z.any()).min(1, "Sube al menos una imagen"),
  primaryImageIndex: z.number().default(0),
  technicalSpecFile: z.array(z.any()).optional(),
})

export const updateProductSchema = productSchema.extend({
  imageFiles: z.array(z.any()).optional(),
  initialStock: z.coerce.number().int().min(0).optional(),
})

export type ProductFormData = z.infer<typeof productSchema>
export type UpdateProductFormData = z.infer<typeof updateProductSchema>
export const contactSchema = z.object({
  name: z.string().min(2, "Nombre debe tener al menos 2 caracteres").transform(sanitizeString),
  email: z.string().email("Email debe ser válido").transform(sanitizeEmail),
  subject: z.string().min(3, "Asunto debe tener al menos 3 caracteres").transform(sanitizeString),
  message: z.string().min(10, "Mensaje debe tener al menos 10 caracteres").transform(sanitizeString),
  honeypot: z.string().optional(),
  turnstileToken: z.string().optional(),
})

export type ContactFormData = z.infer<typeof contactSchema>

